import { useEffect, useState } from 'react'

import styled from '@emotion/styled'
import { Heading, PureInput, Text } from '@hhgtech/hhg-components/components'
import { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import { MediaQueries, theme } from '@hhgtech/hhg-components/misc'
import innertext from 'innertext'
import throttle from 'lodash/throttle'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Draggable from 'react-draggable' // DraggableEventHandler, // DraggableEvent, // DraggableData,
import { useIntl } from 'react-intl'

import { Container } from 'components/atoms/container'
import GoTopButton from 'components/molecules/goTopButton'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import { NavigatorLayout } from 'components/templates/navigatorLayout'
import { useIsMobile } from 'hooks/useIsMobile'
import { LOCALE_SPECS, assetImagePath } from 'interfaces/constants'
import {
  Category,
  DrugItemApi,
  FooterInfo,
  SiteWideBanner,
} from 'interfaces/types'
import { fastdom } from 'utils/fastdom'

import { StyledSearchDrug } from './index.styled'

export type Props = {
  page: string
  isMobileSsr: boolean
  items: {
    [id: string]: {
      label: string
      posts: Array<DrugItemApi>
    }
  }
  info: Category
  footerInfo: FooterInfo
  dataNavMenuContent?: NavMenuContentType
  sitewideBanner?: SiteWideBanner | null
}

const DrugsPageComponentProps = ({
  items,
  info,
  footerInfo,
  dataNavMenuContent,
}: Props) => {
  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()
  const { locale = 'en-PH' } = useRouter()

  const aZ = Object.keys(items)
  const [currentAlphabet, setCurrentAlphabet] = useState<string | undefined>(
    undefined,
  )
  const [currentFilter, setCurrentFilter] = useState('')
  const [isFiltering, setIsFiltering] = useState(false)
  const [isSticky, setIsSticky] = useState(false)

  // const handleDrag: DraggableEventHandler = (
  //   __e: DraggableEvent,
  //   data: DraggableData,
  // ) => {
  //   if (
  //     !!currentAlphabet &&
  //     Math.abs(data.y / 17 - aZ.indexOf(currentAlphabet)) <= 3
  //   ) {
  //     setCurrentAlphabet(aZ[data.y / 17])
  //   }
  // }

  useEffect(() => {
    if (!!currentAlphabet && typeof window !== 'undefined') {
      const current =
        currentAlphabet === 'other' ? aZ[aZ.length - 2] : currentAlphabet
      fastdom.measure(() => {
        const element = document.querySelector(`#drug-${current}`)
        if (element) {
          const headerOffset = isMobile ? 72 : 145
          const offsetPosition =
            element.getBoundingClientRect().top +
            window.pageYOffset -
            headerOffset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'auto',
          })
        }
      })
    }
  }, [currentAlphabet])

  useEffect(() => {
    const handleScroll = throttle(() => {
      const firstDrug = document.querySelector(`#drug-${aZ[0]}`)
      if (firstDrug) {
        const y = firstDrug.getBoundingClientRect().y
        if (y <= 80) {
          setIsSticky(true)
        } else {
          setIsSticky(false)
        }
      }
    }, 300)

    if (isMobile) {
      window.addEventListener('scroll', handleScroll, { passive: true })
    }

    return () => {
      if (isMobile) {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  useEffect(() => {
    setIsFiltering(!!currentFilter && currentFilter.length > 0)
  }, [currentFilter])

  return (
    <NavigatorLayout
      title={`${info.displayName} - ${LOCALE_SPECS[locale].SITE_NAME_FORMATTED}`}
      description={info.description}
      image={info.featureImage}
      SEOData={{
        isIndexable: true,
      }}
      footerInfo={footerInfo}
      dataNavMenuContent={dataNavMenuContent}
    >
      <div>
        <StyledThumbnail>
          <Container
            className="thumbnail-inner"
            style={{ backgroundImage: `url(${assetImagePath}/temp_drugs.png)` }}
          ></Container>
        </StyledThumbnail>
        <StyledDrugs>
          <StyledInfoWrapper>
            <Heading
              tag="h2"
              as="h1"
              color={info.textColor}
              className="category-heading"
            >
              {info.displayName}
            </Heading>
            <PureInput
              className={'search-input'}
              name="drugs-search"
              type="search"
              size="md"
              placeholder={f({
                id: 'drugPage.searchPlaceholder',
                defaultMessage: 'Search by Drug, Condition or Category',
              })}
              onChange={(v) => setCurrentFilter(v.toLowerCase())}
            />
          </StyledInfoWrapper>
          <StyledResultsContainer>
            <StyledResultsInner>
              {aZ.map((v) => (
                <div key={`drug-${v}`} id={`drug-${v}`}>
                  {!isFiltering && (
                    <StyledDrugHeading>
                      <Heading tag="h5">{v}</Heading>
                    </StyledDrugHeading>
                  )}
                  {!isMobile && (
                    <StyledSearchDrug>
                      {items[v].posts
                        .filter((p: DrugItemApi) =>
                          p.title.toLowerCase().includes(currentFilter),
                        )
                        .map((p: DrugItemApi) => (
                          <StyledLiItem key={p.permalink}>
                            <Heading tag="h5">
                              <Link prefetch={false} href={p.permalink}>
                                {innertext(p.title) || p.permalink}
                              </Link>
                            </Heading>
                          </StyledLiItem>
                        ))}
                    </StyledSearchDrug>
                  )}
                  {isMobile &&
                    items[v].posts
                      .filter((p: DrugItemApi) =>
                        p.title.toLowerCase().includes(currentFilter),
                      )
                      .map((p: DrugItemApi) => (
                        <StyledLiItem key={p.permalink}>
                          <Heading tag="h5">
                            <Link prefetch={false} href={p.permalink}>
                              {innertext(p.title) || p.permalink}
                            </Link>
                          </Heading>
                        </StyledLiItem>
                      ))}
                </div>
              ))}
            </StyledResultsInner>
            {!isFiltering && (
              <StyledScrollList
                data-visible={isSticky || undefined}
                theme={{ height: `${aZ.length * 17}px` }}
              >
                {isMobile &&
                  aZ.map((v, i) => (
                    <Draggable
                      key={`d-${v}`}
                      axis="y"
                      handle=".handle"
                      bounds="parent"
                      // onDrag={handleDrag}
                      grid={[8, 17]}
                      position={{ x: 0, y: i * 17 }}
                      onMouseDown={() => setCurrentAlphabet(aZ[i])}
                    >
                      <StyledScroller className="handle" />
                    </Draggable>
                  ))}
                {aZ.map((v, i) => (
                  <StyledAlphabet
                    key={v + i}
                    data-active={v === currentAlphabet || undefined}
                    onMouseDown={() => setCurrentAlphabet(aZ[i])}
                  >
                    <Text
                      type="caption"
                      size="c2"
                      color={v === currentAlphabet ? '#2D87F3' : '#262626'}
                    >
                      {v === 'other' ? '#' : v}
                    </Text>
                  </StyledAlphabet>
                ))}
              </StyledScrollList>
            )}
          </StyledResultsContainer>
        </StyledDrugs>
        <GoTopButton />

        <MobileBottomNavigationWrapper />
      </div>
    </NavigatorLayout>
  )
}

const StyledDrugs = styled.div`
  position: relative;

  ${MediaQueries.mbUp} {
    padding: 24px 0;
    max-width: 1170px;
    margin: 0 auto;
  }

  .category-heading {
    padding: 0 16px;
    margin: 8px 0 16px 0;

    ${MediaQueries.mbUp} {
      padding: 0;
    }
  }

  .search-input {
    padding: 0 16px 24px 16px;

    ${MediaQueries.mbUp} {
      padding: 0;
    }
  }
`

const StyledInfoWrapper = styled.div`
  ${MediaQueries.mbUp} {
    position: absolute;
    z-index: 1;
    min-width: 568px;
    top: -154px;
  }
`

const StyledThumbnail = styled.div`
  padding-top: 80px;
  background-color: ${theme.colors.primary50};
  ${MediaQueries.mbUp} {
    padding-top: 0;
  }

  .thumbnail-inner {
    width: 100%;
    height: 150px;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: 150px;

    ${MediaQueries.mbUp} {
      height: 194px;
      padding-top: 24px;
      background-position: center right;
    }
  }
`

const StyledResultsContainer = styled.div`
  position: relative;
  display: flex;

  ${MediaQueries.mbUp} {
    flex-direction: column-reverse;
  }
`

const StyledResultsInner = styled.div`
  max-width: calc(100vw - 17px);
  flex: 1;
`

const StyledScrollList = styled.div`
  position: sticky;
  z-index: 100;
  top: calc((100vh - ${(props: any) => props.theme?.height}) / 2);
  right: 4px;
  display: none;
  width: 25px;
  height: ${(props: any) => props.theme?.height};
  flex-direction: column;
  align-items: center;

  ${MediaQueries.mbUp} {
    position: sticky;
    display: flex;
    top: 64px;
    flex-direction: row;
    right: 0;
    width: 100%;
    height: auto;
    padding: 24px 0;
    background-color: #fff;
  }

  &[data-visible] {
    display: flex;
  }
`

const StyledScroller = styled.div`
  position: absolute;
  z-index: 1000;
  top: 0;
  width: 25px;
  height: 17px;
  cursor: move;
`

const StyledAlphabet = styled.div`
  width: 8px;
  height: 17px;
  text-align: center;

  &[data-active] {
    color: ${theme.colors.primaryActive};
  }

  ${MediaQueries.mbUp} {
    width: 35px;
    height: 35px;
    background-color: #f7f9fc;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
    border-radius: ${theme.borderRadius};
  }
`

const StyledDrugHeading = styled.div`
  display: flex;
  height: 32px;
  align-items: center;
  padding: 0 16px;
  background-color: ${theme.colors.neutral50};
  text-transform: uppercase;

  ${MediaQueries.mbUp} {
    background-color: transparent;
    border-bottom: solid 1px #e4e8ec;
    margin: 24px 0;
    padding: 0 0 8px 0;
  }
`

const StyledLiItem = styled.li`
  padding: 12px 16px;
  list-style: none;
  overflow-wrap: break-word;
  text-decoration: none;
  ${MediaQueries.mbUp} {
    width: 25%;
    padding: 12px;
  }

  ${MediaQueries.mbDown} {
    &:not(:last-child) {
      border-bottom: solid 1px #f8f9fc;
    }
  }

  a {
    color: inherit;
    text-decoration: none;
  }
`

export default DrugsPageComponentProps
